import type { NotifyPort } from '@/features/messages/domain/notify'
import type { TransitionTarget, TransitionTrigger, WorkflowPort } from '../domain/workflow'

type TransitionSave = {
	existingId: string | null
	isDefault: boolean
	label: string
	trigger: TransitionTrigger
	target: TransitionTarget
}

type UpdateStepWithTransitionsInput = {
	stepId: string
	name: string
	description: string | null
	removedTransitionIds: string[]
	transitions: TransitionSave[]
}

// Orchestrates the full step-edit save: update step metadata, delete removed
// transitions, then update or create each remaining transition in parallel.
// A single notify call fires at the end so the UI doesn't get a premature
// success message while half the transition writes are still in-flight.
const updateStepWithTransitions = async (
	port: WorkflowPort,
	input: UpdateStepWithTransitionsInput,
	notify: NotifyPort,
): Promise<boolean> => {
	try {
		await port.updateStep({ id: input.stepId, name: input.name, description: input.description })

		if (input.removedTransitionIds.length > 0) {
			await Promise.all(input.removedTransitionIds.map(id => port.deleteTransition(id)))
		}

		if (input.transitions.length > 0) {
			await Promise.all(
				input.transitions.map(t =>
					t.existingId
						? port.updateTransition({ id: t.existingId, label: t.label, trigger: t.trigger, target: t.target })
						: port.createTransition({
								fromStepId: input.stepId,
								label: t.label,
								trigger: t.trigger,
								target: t.target,
								isDefault: t.isDefault,
							}),
				),
			)
		}

		notify.success('workflow.stepUpdated')
		return true
	} catch {
		notify.error('workflow.stepUpdateFailed')
		return false
	}
}

export type { TransitionSave, UpdateStepWithTransitionsInput }
export { updateStepWithTransitions }
